import React, { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'
import { rem } from 'polished'

import config from 'constants/config'
import zIndex from 'styles/tools/z-index'
import { bookingDesktopUp } from 'components/Booking/BookingSidebar/styles'

import { useAppDispatch } from 'hooks/reduxHooks'
import { goBack } from 'connected-react-router'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import { setDevToolsEnabled } from 'actions/CheckoutActions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import CommonHeaderCheckout from './CommonHeaderCheckout'
import StepsHeaderCheckout from './StepsHeaderCheckout/StepsHeaderCheckout'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import {
  hasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade,
} from 'checkout/selectors/tourV2Selectors'

const Container = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${() => rem(config.header.checkout.height)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  padding: 0 ${rem(16)};
  z-index: ${zIndex.bookingSidebar - 1};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  ${mediaQueryUp.largeDesktop} {
    padding: 0 ${rem(50)};
  }

  ${bookingDesktopUp} {
    z-index: ${zIndex.bookingSidebar + 1};
  }

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundColor && css`
    background: ${config.header.backgroundColor};
    color: ${props => props.theme.palette.neutral.default.eight};
  `}

  ${() => !config.WL_USES_GLOBAL_STYLING && config.header.backgroundGradientColor && css`
    background: linear-gradient(90deg, ${config.header.backgroundColor}, ${config.header.backgroundGradientColor});
  `}
`

interface Props {
  isDevToolsEnabled: boolean
  isCruise: boolean
  isTour: boolean
}

function HeaderCheckout(props: Props) {
  const { isDevToolsEnabled, isCruise, isTour } = props

  const dispatch = useAppDispatch()
  const onBack = () => {
    dispatch(goBack())
  }

  const onToggleDevTools = useCallback(() => {
    dispatch(setDevToolsEnabled(!isDevToolsEnabled))
  }, [dispatch, isDevToolsEnabled])

  const headerRef = useRef<HTMLElement>(null)
  useHeaderStateObserver('checkout', headerRef)

  const showStepsHeader = (isCruise && config.CRUISES_NEW_CHECKOUT_ENABLED) || (isTour && config.TOUR_V2_NEW_CHECKOUT_ENABLED)
  const showCommonHeader = !showStepsHeader

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.checkout}>
      <Container data-testid="checkout-header" ref={headerRef}>
        {showCommonHeader &&
          <CommonHeaderCheckout
            isDevToolsEnabled={isDevToolsEnabled}
            onBack={onBack}
            onToggleDevTools={onToggleDevTools}
          />
        }

        {showStepsHeader &&
          <StepsHeaderCheckout
            isDevToolsEnabled={isDevToolsEnabled}
            onBack={onBack}
            onToggleDevTools={onToggleDevTools}
          />
        }
      </Container>
    </AnalyticsPageContext.Provider>
  )
}

const mapStateToProps = (state: App.State) => ({
  isDevToolsEnabled: state.checkout.cart.isDevToolsEnabled,
  isCruise: getCruiseItems(state).length > 0,
  isTour: hasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade(state),
})

export default connect(mapStateToProps)(HeaderCheckout)
